import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { Package, Laptop, Monitor, Smartphone, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetPortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssetData();
  }, [impersonatedUser]);

  const fetchAssetData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/assets/${impersonatedUser.employee_id}`,
        config
      );
      
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching asset data:', error);
      toast.error('Unable to fetch asset data');
    } finally {
      setLoading(false);
    }
  };

  const getAssetIcon = (type) => {
    const lowerType = (type || '').toLowerCase();
    if (lowerType.includes('laptop')) return <Laptop className="w-4 h-4 text-blue-600" />;
    if (lowerType.includes('monitor')) return <Monitor className="w-4 h-4 text-green-600" />;
    if (lowerType.includes('phone') || lowerType.includes('mobile')) return <Smartphone className="w-4 h-4 text-purple-600" />;
    return <Package className="w-4 h-4 text-gray-600" />;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'in_use': 'bg-green-100 text-green-800',
      'available': 'bg-blue-100 text-blue-800',
      'under_maintenance': 'bg-yellow-100 text-yellow-800',
      'retired': 'bg-gray-100 text-gray-800',
      'lost': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="asset-portal-embed">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="w-5 h-5 text-cyan-600" />
            Assigned Assets for {impersonatedUser.name} ({assets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No assets assigned</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map(asset => (
                  <TableRow key={asset.id || asset.asset_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getAssetIcon(asset.asset_type)}
                        <span className="font-medium">{asset.asset_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>{asset.asset_type}</TableCell>
                    <TableCell className="font-mono text-sm">{asset.serial_number || '-'}</TableCell>
                    <TableCell>{asset.manufacturer || '-'}</TableCell>
                    <TableCell>{asset.model || '-'}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(asset.status)}>
                        {(asset.status || 'unknown').replace('_', ' ')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetPortalEmbed;
